/**
 * Pestaña "Mi grupo" de Solicitudes (spec 2026-09-28-grupos-supervisoras §7):
 * disponibilidad, suplente, sus contadoras y la tabla de todos los grupos.
 * Cada cambio se guarda al instante. Root puede editar el grupo de cualquiera
 * eligiéndolo en la tabla.
 */

import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert, Button, Card, List, Select, Space, Switch, Table, Tag, Typography, notification } from 'antd';

import { supervisorGroups } from '../../actions/database/supervisor_groups';
import { classifyUsers, validateGroup, initialGroupSid } from '../../lib/supervisor_groups';
import { friendlyError } from '../../lib/deletion_request';

const EMPTY = { members: [], available: true, backupUid: null };

export const MyGroup = ({ directory, myUid, isRoot }) => {
  const dispatch = useDispatch();
  const { supervisors, privileged, candidates } = useMemo(
    () => classifyUsers(directory.users, directory.rolesById), [directory.users, directory.rolesById]);
  // Root sin rol de supervisora no tiene grupo propio: arranca en la primera.
  const [sid, setSid] = useState(() => initialGroupSid({ myUid, supervisors }));
  const [saving, setSaving] = useState(false);
  const iAmSupervisor = supervisors.some((s) => s.uid === myUid);
  const byUid = useMemo(() => new Map(directory.users.map((u) => [u.uid, u])), [directory.users]);
  const groupOf = (uid) => directory.groups.find((g) => g.uid === uid) || EMPTY;
  const group = groupOf(sid);
  const nameOf = (uid) => byUid.get(uid)?.name || uid;
  const editingOther = sid !== myUid;

  const save = async (patch) => {
    const next = { ...EMPTY, ...group, ...patch };
    const error = validateGroup({ sid, members: next.members, backupUid: next.backupUid, privileged });
    if (error) { notification.error({ message: 'No se guardó', description: error, placement: 'topRight' }); return; }
    setSaving(true);
    try {
      await dispatch(supervisorGroups.save({ sid, members: next.members, available: next.available, backupUid: next.backupUid }));
    } catch (e) {
      notification.error({ message: 'No se guardó', description: friendlyError(e, 'Intenta de nuevo.'), placement: 'topRight' });
    } finally {
      setSaving(false);
    }
  };

  const backupOptions = [
    { value: '', label: 'Todas las demás supervisoras' },
    ...supervisors.filter((s) => s.uid !== sid).map((s) => ({ value: s.uid, label: s.name })),
  ];
  const addOptions = candidates
    .filter((u) => !(group.members || []).includes(u.uid))
    .map((u) => ({ value: u.uid, label: `${u.name} — ${u.email}` }));

  const allGroups = supervisors.map((s) => {
    const g = groupOf(s.uid);
    return {
      key: s.uid,
      name: s.name,
      available: g.available !== false,
      backup: g.available === false ? (g.backupUid ? nameOf(g.backupUid) : 'Todas las demás') : '—',
      members: (g.members || []).map(nameOf),
    };
  });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {!sid ? (
        <Alert type="info" showIcon message="No hay supervisoras asignadas. Los avisos de solicitudes les llegan a los usuarios root." />
      ) : null}
      {sid && editingOther ? (
        <Alert type="info" showIcon message={`Estás editando el grupo de ${nameOf(sid)}.`}
          action={iAmSupervisor ? <Button size="small" onClick={() => setSid(myUid)}>Volver al mío</Button> : null} />
      ) : null}

      {sid ? (
      <>
      <Card title={editingOther ? `Disponibilidad de ${nameOf(sid)}` : 'Mi disponibilidad'} size="small">
        <Space direction="vertical">
          <Space>
            <Switch checked={group.available !== false} loading={saving}
              onChange={(checked) => save({ available: checked })} />
            <Typography.Text>{group.available !== false ? 'Disponible' : 'No estoy disponible'}</Typography.Text>
          </Space>
          <Space>
            <Typography.Text>Suplente mientras no está:</Typography.Text>
            <Select style={{ minWidth: 260 }} value={group.backupUid || ''} options={backupOptions} disabled={saving}
              onChange={(v) => save({ backupUid: v || null })} />
          </Space>
        </Space>
      </Card>

      <Card title={editingOther ? `Contadoras de ${nameOf(sid)}` : 'Mis contadoras'} size="small">
        <List
          size="small"
          locale={{ emptyText: 'Todavía no hay nadie en este grupo' }}
          dataSource={group.members || []}
          renderItem={(uid) => (
            <List.Item actions={[
              <Button key="q" type="link" danger disabled={saving}
                onClick={() => save({ members: (group.members || []).filter((m) => m !== uid) })}>Quitar</Button>,
            ]}>
              <List.Item.Meta title={nameOf(uid)} description={byUid.get(uid)?.email || 'Usuario que ya no existe'} />
            </List.Item>
          )}
        />
        <Select
          showSearch
          style={{ width: '100%', marginTop: 8 }}
          placeholder="Agregar por nombre o correo"
          value={null}
          options={addOptions}
          disabled={saving}
          filterOption={(input, opt) => String(opt?.label || '').toLowerCase().includes(input.toLowerCase())}
          onChange={(uid) => save({ members: [...(group.members || []), uid] })}
        />
      </Card>

      </>
      ) : null}

      <Card title="Todos los grupos" size="small">
        <Table
          size="small"
          pagination={false}
          dataSource={allGroups}
          columns={[
            { title: 'Supervisora', dataIndex: 'name', key: 'name' },
            { title: 'Estado', key: 'av', render: (_, r) => (r.available ? <Tag color="green">Disponible</Tag> : <Tag color="orange">No disponible</Tag>) },
            { title: 'La cubre', dataIndex: 'backup', key: 'backup' },
            { title: 'Contadoras', key: 'm', render: (_, r) => (r.members.length ? r.members.join(', ') : '—') },
            ...(isRoot ? [{
              title: '', key: 'edit',
              render: (_, r) => <Button size="small" onClick={() => setSid(r.key)}>Editar</Button>,
            }] : []),
          ]}
        />
      </Card>
    </Space>
  );
};

export default MyGroup;
